import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { TID } from "@/constants/testIds";
import { Wand2, Trash2, Plus, X, Sparkles, Check, ChevronDown, ChevronRight, Bot, ArrowUp, ArrowDown, Copy, Power, Pencil, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import QuickCreateModal from "@/components/QuickCreateModal";
import CopyRuleModal from "@/components/CopyRuleModal";

// Source-filter chips. Each entry drives:
//   • the label shown in the chip row,
//   • which rule `source`/`created_by` values it matches,
//   • the visual tone for the badge in the Source column.
// Matcher runs against `rule.source` first (new), then `rule.created_by`
// (legacy AI miner data), then `undefined` == human authored.
const SOURCE_FILTERS = [
  { key: "all",              label: "All",                match: () => true },
  { key: "cpa_from_answer",  label: "From client answer", match: r => r.source === "cpa_from_answer" },
  { key: "ai_miner",         label: "AI miner",           match: r => r.source === "ai_miner" || r.created_by === "ai_miner" },
  { key: "ai",               label: "AI-assisted",        match: r => r.created_by === "ai" && r.source !== "cpa_from_answer" && r.source !== "ai_miner" },
  { key: "human",            label: "Human",              match: r => !r.source && (!r.created_by || r.created_by === "human") },
];

export default function Rules() {
  const { currentId } = useCompany();
  const [searchParams, setSearchParams] = useSearchParams();
  // The chip that's currently on. Seeded from `?source=<key>` so a
  // deep-link like `/accounting/rules?source=cpa_from_answer` boots
  // directly into the filtered view. Written back on chip changes so
  // sharing the URL preserves the filter.
  const [sourceFilter, setSourceFilter] = useState(searchParams.get("source") || "all");
  const [rules, setRules] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [accts, setAccts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [classes, setClasses] = useState([]);
  const [tags, setTags] = useState([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const load = async () => {
    if (!currentId) return;
    const [r, a, c, cl, tg] = await Promise.all([
      api.get(`/companies/${currentId}/rules`),
      api.get(`/companies/${currentId}/accounts`),
      api.get(`/companies/${currentId}/contacts?limit=500`).catch(() => ({ data: {} })),
      api.get(`/companies/${currentId}/classes`).catch(() => ({ data: {} })),
      api.get(`/companies/${currentId}/tags`).catch(() => ({ data: {} })),
    ]);
    setRules(r.data.rules || []);
    setCandidates(r.data.candidates || []);
    setAccts(a.data.accounts || []);
    setContacts((c.data?.contacts || []).map(x => ({ id: x.id, name: x.name })));
    setClasses((cl.data?.classes || cl.data?.items || []).map(x => ({ id: x.id, name: x.name })));
    setTags((tg.data?.tags || tg.data?.items || []).map(x => ({ id: x.id, name: x.name })));
  };
  useEffect(() => { load(); }, [currentId]);

  const del = async (id) => {
    if (!confirm("Delete rule?")) return;
    await api.delete(`/companies/${currentId}/rules/${id}`);
    load();
  };

  // Tier-3: partial update via PATCH — enable toggle, priority nudge.
  const patchRule = async (id, patch) => {
    try {
      await api.patch(`/companies/${currentId}/rules/${id}`, patch);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  // Priority nudge — swaps this rule with the one immediately above /
  // below it in the current sort (priority DESC). Uses PATCH so the
  // matcher immediately sees the new order on the next ingest.
  const bumpPriority = (id, delta) => {
    const sorted = [...rules].sort(
      (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
    );
    const idx = sorted.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const next = idx + (delta > 0 ? -1 : 1);      // up in list = +priority
    if (next < 0 || next >= sorted.length) return;
    const a = sorted[idx], b = sorted[next];
    const aPri = a.priority ?? 0, bPri = b.priority ?? 0;
    // If ties, bump by one to move it above/below.
    const newA = aPri === bPri ? (delta > 0 ? bPri + 1 : bPri - 1) : bPri;
    patchRule(a.id, { priority: newA });
  };

  const [copyRule, setCopyRule] = useState(null);   // {rule}
  const [editRule, setEditRule] = useState(null);   // rule being edited via popup
  const [needsReviewRules, setNeedsReviewRules] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  useEffect(() => {
    if (!currentId) return;
    let cancel = false;
    api.get(`/companies/${currentId}/rules/needs-review`)
      .then(r => { if (!cancel) setNeedsReviewRules(r.data?.rules || []); })
      .catch(() => { if (!cancel) setNeedsReviewRules([]); });
    return () => { cancel = true; };
  }, [currentId, rules.length]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = (key) => setCollapsedGroups(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const promoteCandidate = async (c) => {
    try {
      const r = await api.post(`/companies/${currentId}/rules`, {
        match_type: "merchant_contains", match_value: c.merchant,
        account_code: c.account_code, apply_to_existing: true,
      });
      toast.success(
        `Rule created: "${c.merchant}" → ${c.account_name}`
        + (r.data.applied ? ` (applied to ${r.data.applied} txn${r.data.applied === 1 ? "" : "s"})` : "")
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create rule");
    }
  };

  const dismissCandidate = async (c) => {
    await api.delete(`/companies/${currentId}/rule-candidates/${c.id}`);
    toast.success(`Dismissed suggestion for "${c.merchant}"`);
    load();
  };

  const acceptAll = async () => {
    if (!candidates.length) return;
    setBusy(true);
    let ok = 0, fail = 0, applied = 0;
    for (const c of candidates) {
      try {
        const r = await api.post(`/companies/${currentId}/rules`, {
          match_type: "merchant_contains", match_value: c.merchant,
          account_code: c.account_code, apply_to_existing: true,
        });
        ok += 1;
        applied += r.data.applied || 0;
      } catch {
        fail += 1;
      }
    }
    setBusy(false);
    toast.success(
      `Accepted ${ok} rule${ok === 1 ? "" : "s"}`
      + (applied ? ` · back-filled ${applied} txn${applied === 1 ? "" : "s"}` : "")
      + (fail ? ` · ${fail} failed` : "")
    );
    load();
  };

  const totalCleanup = candidates.reduce((s, c) => s + (c.applies_to_count || 0), 0);

  // Chip counts + client-side filter. Counts are always over the full
  // rules list so a chip shows its size even when a different chip is
  // active.
  const sourceCounts = useMemo(() => {
    const out = {};
    for (const f of SOURCE_FILTERS) out[f.key] = 0;
    for (const r of rules) {
      for (const f of SOURCE_FILTERS) {
        if (f.match(r)) out[f.key] += 1;
      }
    }
    return out;
  }, [rules]);

  const visibleRules = useMemo(() => {
    const f = SOURCE_FILTERS.find(x => x.key === sourceFilter) || SOURCE_FILTERS[0];
    return rules.filter(f.match);
  }, [rules, sourceFilter]);

  const onSourceFilterChange = (key) => {
    setSourceFilter(key);
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("source");
    else next.set("source", key);
    setSearchParams(next, { replace: true });
  };

  // Bulk toggle helper — flips `enabled` on every rule matching the
  // active source filter. Only offered for non-"all"/"human" filters
  // so we don't accidentally offer a "disable every hand-written rule"
  // action from a filter that's just "no source tagged".
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkToggleSource = async (targetEnabled) => {
    if (!currentId) return;
    const filterKey = sourceFilter;
    const supported = ["cpa_from_answer", "ai_miner"];
    if (!supported.includes(filterKey)) return;
    const filterDef = SOURCE_FILTERS.find(f => f.key === filterKey);
    const filterLabel = filterDef?.label || filterKey;
    const affected = rules.filter(r => filterDef.match(r) && !!r.enabled !== targetEnabled);
    if (affected.length === 0) {
      toast.info("Nothing to change.");
      return;
    }
    const verb = targetEnabled ? "re-enable" : "disable";
    if (!window.confirm(
      `${verb === "disable" ? "Disable" : "Re-enable"} all ${affected.length} rule${affected.length === 1 ? "" : "s"} tagged "${filterLabel}"? This is reversible.`
    )) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/companies/${currentId}/rules/bulk-toggle`, {
        source: filterKey,
        enabled: targetEnabled,
      });
      toast.success(`${verb === "disable" ? "Disabled" : "Re-enabled"} ${r.data.modified} rule${r.data.modified === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk toggle failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">AI Rules</h1>
          <p className="text-slate-500 text-sm mt-1">Rules automate categorization. Auto-suggested when a merchant is approved 2+ times.</p>
        </div>
        <button data-testid={TID.addBtn} onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs">
          <Plus size={13} /> Create Rule
        </button>
      </div>

      {!bannerDismissed && needsReviewRules.length > 0 && (
        <div
          data-testid="rules-needs-review-banner"
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3"
        >
          <span className="text-lg" role="img" aria-hidden>⚠️</span>
          <div className="flex-1 text-sm text-amber-900">
            <b>{needsReviewRules.length} rule{needsReviewRules.length === 1 ? "" : "s"} may need review</b>
            {" — "}
            {needsReviewRules.slice(0, 2).map((r, i) => (
              <span key={r.id}>
                {i > 0 && ", "}
                <b>{r.match_value}</b>
                {` was reclassified ${r.reclassified_recent} of ${r.reclassified_recent + r.fired_recent} times`}
              </span>
            ))}
            {needsReviewRules.length > 2 && `, and ${needsReviewRules.length - 2} more`}
            .
          </div>
          <div className="flex items-center gap-1">
            {needsReviewRules.length === 1 && (
              <button
                onClick={() => {
                  const target = rules.find(x => x.id === needsReviewRules[0].id);
                  if (target) setEditRule(target);
                }}
                data-testid="rules-needs-review-review"
                className="px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs"
              >
                Review
              </button>
            )}
            <button
              onClick={() => setBannerDismissed(true)}
              data-testid="rules-needs-review-dismiss"
              className="px-2.5 py-1 rounded text-amber-800 hover:bg-amber-100 text-xs"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {candidates.length > 0 && (
        <div className="rounded-xl border bg-indigo-50 border-indigo-200" data-testid="suggested-rules">
          <button
            onClick={() => setExpanded(v => !v)}
            className="w-full px-4 py-3 flex items-center gap-2 text-left"
            data-testid="suggested-rules-toggle"
          >
            <Sparkles size={14} className="text-indigo-600" />
            <h3 className="font-heading font-semibold text-sm">
              AI suggests {candidates.length} new rule{candidates.length === 1 ? "" : "s"}
            </h3>
            {totalCleanup > 0 && (
              <span className="text-[11px] text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">
                would clean up {totalCleanup} un-reviewed txn{totalCleanup === 1 ? "" : "s"}
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {candidates.length > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); acceptAll(); }}
                  disabled={busy}
                  data-testid="suggested-rules-accept-all"
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Check size={12} /> Accept all
                </button>
              )}
              <ChevronDown
                size={16}
                className={`text-indigo-600 transition-transform ${expanded ? "" : "-rotate-90"}`}
              />
            </div>
          </button>

          {expanded && (
            <div className="px-4 pb-4 space-y-2">
              {candidates.map(c => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 bg-white rounded-md px-3 py-2 border"
                  data-testid={`suggested-rule-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">
                      When merchant contains <b>{c.merchant}</b> → <b className="tabular-nums">{c.account_code}</b> {c.account_name}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      approved {c.approvals}×
                      {(c.applies_to_count ?? 0) > 0 && (
                        <> · would back-fill <b className="text-indigo-700">{c.applies_to_count}</b> un-reviewed txn{c.applies_to_count === 1 ? "" : "s"}</>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => promoteCandidate(c)}
                    data-testid={`suggested-rule-accept-${c.id}`}
                    className="text-xs px-2.5 py-1 rounded-md bg-slate-900 text-white hover:bg-slate-800"
                  >
                    Create rule
                  </button>
                  <button
                    onClick={() => dismissCandidate(c)}
                    data-testid={`suggested-rule-dismiss-${c.id}`}
                    className="text-xs p-1 text-slate-500 hover:text-rose-600"
                    title="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border bg-white overflow-hidden">
        {/* Source-filter chip row. Lives inside the table shell so
            filters feel scoped to the list they're filtering. */}
        <div className="border-b bg-slate-50/60 px-3 py-2 flex items-center gap-1.5 flex-wrap" data-testid="rules-source-chips">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mr-1">Source</span>
          {SOURCE_FILTERS.map(f => {
            const on = sourceFilter === f.key;
            const count = sourceCounts[f.key] ?? 0;
            return (
              <button
                key={f.key}
                onClick={() => onSourceFilterChange(f.key)}
                className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${on
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                data-testid={`rules-source-chip-${f.key}`}
              >
                {f.label}
                <span className={`text-[10px] font-mono-num px-1 rounded ${on ? "bg-white/25 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {/* Bulk unwind bar — only when a bulk-toggleable source filter is
            active AND we actually have matching rules. Split enabled vs
            disabled counts so we can offer the correct primary action
            (or both, when there's a mix). */}
        {(() => {
          const supported = ["cpa_from_answer", "ai_miner"];
          if (!supported.includes(sourceFilter)) return null;
          if (visibleRules.length === 0) return null;
          const enabledCount  = visibleRules.filter(r => !!r.enabled).length;
          const disabledCount = visibleRules.length - enabledCount;
          const filterLabel   = SOURCE_FILTERS.find(f => f.key === sourceFilter)?.label || sourceFilter;
          return (
            <div
              className="border-b bg-amber-50 px-3 py-2 flex items-center gap-3 flex-wrap"
              data-testid="rules-bulk-unwind-bar"
            >
              <span className="text-lg" role="img" aria-hidden>↩️</span>
              <div className="flex-1 text-xs text-amber-900">
                <b>{visibleRules.length} rule{visibleRules.length === 1 ? "" : "s"} in this batch</b>
                {" — "}
                {enabledCount > 0 && (<>{enabledCount} enabled</>)}
                {enabledCount > 0 && disabledCount > 0 && ", "}
                {disabledCount > 0 && (<>{disabledCount} disabled</>)}.
                {" "}Made a mistake on a recent "{filterLabel}" sweep? Disable them here — deletion isn't needed and the audit trail stays intact.
              </div>
              <div className="flex items-center gap-1.5">
                {enabledCount > 0 && (
                  <button
                    onClick={() => bulkToggleSource(false)}
                    disabled={bulkBusy}
                    className="text-xs px-3 py-1 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="rules-bulk-disable-all"
                  >
                    <Power size={11} /> Disable all {enabledCount}
                  </button>
                )}
                {disabledCount > 0 && (
                  <button
                    onClick={() => bulkToggleSource(true)}
                    disabled={bulkBusy}
                    className="text-xs px-3 py-1 rounded-md bg-white text-amber-900 border border-amber-300 hover:bg-amber-100 disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="rules-bulk-enable-all"
                  >
                    <Power size={11} /> Re-enable all {disabledCount}
                  </button>
                )}
              </div>
            </div>
          );
        })()}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b">
            <tr>
              <th className="px-3 py-2 text-left">Match</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-right">Applied</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // ── Group rules by contact + sort alphabetically ─────────
              // - Contact-keyed rules become group leaders, displayed as
              //   "contact_equals · <contact name>" instead of the raw
              //   UUID.
              // - Merchant-keyed rules whose match_value maps to a known
              //   contact (case-insensitive substring either direction)
              //   are nested under that contact.
              // - Merchant rules without a contact match render as their
              //   own top-level row.
              // - Groups sort A→Z by display name; children sort A→Z by
              //   match_value.
              const contactsById = new Map(
                (contacts || []).map(c => [c.id, c.name]),
              );
              const contactsByLcName = new Map(
                (contacts || []).map(c => [(c.name || "").toLowerCase(), c]),
              );
              // Fast lookup: which contact does a merchant string map to?
              const findContactForMerchant = (mv) => {
                const s = (mv || "").trim().toLowerCase();
                if (!s) return null;
                // Exact name match first.
                const exact = contactsByLcName.get(s);
                if (exact) return exact;
                // Substring either direction — handles "WALMART" ↔
                // "Walmart" and "Walmart Supercenter" ↔ "Walmart".
                for (const c of contacts || []) {
                  const n = (c.name || "").toLowerCase();
                  if (!n) continue;
                  if (n.includes(s) || s.includes(n)) return c;
                }
                return null;
              };
              const displayNameFor = (r) => {
                if ((r.match_field || "").toLowerCase() === "contact") {
                  return contactsById.get(r.match_value) || r.match_value;
                }
                return r.match_value || "";
              };
              // group_key → { leader: rule|null, name: str, children: rule[] }
              const groups = new Map();
              const ensure = (key, name) => {
                if (!groups.has(key)) {
                  groups.set(key, { leader: null, name, children: [] });
                }
                return groups.get(key);
              };
              for (const r of visibleRules) {
                const isContact = (r.match_field || "").toLowerCase() === "contact";
                if (isContact) {
                  const name = contactsById.get(r.match_value) || r.match_value;
                  const g = ensure(`c:${r.match_value}`, name);
                  // If two contact rules exist for the same contact, the
                  // higher-priority / higher-hits one becomes the leader
                  // and the other is nested as a child.
                  if (!g.leader) g.leader = r;
                  else {
                    const better =
                      ((r.priority ?? 0) > (g.leader.priority ?? 0))
                      || ((r.priority ?? 0) === (g.leader.priority ?? 0)
                          && (r.hits ?? 0) > (g.leader.hits ?? 0));
                    if (better) { g.children.push(g.leader); g.leader = r; }
                    else g.children.push(r);
                  }
                } else {
                  const parent = findContactForMerchant(r.match_value);
                  if (parent) {
                    const g = ensure(`c:${parent.id}`, parent.name);
                    g.children.push(r);
                  } else {
                    // Standalone — key on the raw merchant so a repeated
                    // merchant string still renders as one group.
                    const g = ensure(
                      `m:${(r.match_value || "").toLowerCase()}`,
                      r.match_value || "",
                    );
                    if (!g.leader) g.leader = r;
                    else g.children.push(r);
                  }
                }
              }
              // Sort children within each group alphabetically.
              for (const g of groups.values()) {
                g.children.sort((a, b) =>
                  (a.match_value || "").localeCompare(b.match_value || "", "en", { sensitivity: "base" })
                );
              }
              // Render a single rule row. `isChild` indents the Match
              // cell so nesting reads clearly. `groupKey` + `childCount`
              // + `isLeader` power the group-collapse chevron.
              const renderRow = (r, isChild = false, groupKey = null, childCount = 0, isLeader = false) => {
                const collapsed = groupKey ? collapsedGroups.has(groupKey) : false;
                return (
                <tr key={r.id}
                    className={`border-b hover:bg-slate-50 ${r.enabled === false ? "opacity-50" : ""}`}
                    data-testid={`rule-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className={isChild ? "pl-6 border-l-2 border-slate-200 -ml-3" : "flex items-center gap-1.5"}>
                      {isLeader && childCount > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleGroup(groupKey)}
                          data-testid={`rule-group-toggle-${groupKey}`}
                          className="p-0.5 -ml-1 rounded hover:bg-slate-200 text-slate-500"
                          title={collapsed ? `Show ${childCount} nested rule${childCount === 1 ? "" : "s"}` : "Collapse group"}
                        >
                          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                        </button>
                      )}
                      <div>
                        <span className="text-xs text-slate-500">{r.match_type}</span> · <b>{displayNameFor(r)}</b>
                        {isLeader && childCount > 0 && collapsed && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            +{childCount} nested
                          </span>
                        )}
                        {r.enabled === false && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                            Disabled
                          </span>
                        )}
                        {(r.splits && r.splits.length > 0) && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                            Split · {r.splits.length}-way
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono-num">
                    {(r.splits && r.splits.length > 0)
                      ? (<span className="text-slate-600 font-sans text-xs">
                          {r.splits.map(s => `${s.account_code} (${s.percent}%)`).join(", ")}
                        </span>)
                      : (<>{r.account_code} <span className="text-slate-600 font-sans">{r.account_name}</span></>)
                    }
                  </td>
                  <td className="px-3 py-2">
                    {r.source === "cpa_from_answer" ? (
                      <span
                        data-testid="rule-source-badge"
                        title="Created when a CPA acknowledged a client answer with 'Save rule' checked"
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-200"
                      >
                        <MessageSquare size={9} />
                        From client answer
                      </span>
                    ) : r.created_by === "ai_miner" ? (
                      <span
                        data-testid="rule-source-badge"
                        title={
                          r.mined_confidence
                            ? `Confidence ${(r.mined_confidence * 100).toFixed(0)}% across ${r.hits} historical txns`
                            : "Promoted directly by the AI rules miner"
                        }
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200"
                      >
                        <Bot size={9} />
                        Auto-applied by AI
                      </span>
                    ) : (
                      <span
                        data-testid="rule-source-badge"
                        className={`text-[10px] px-1.5 py-0.5 rounded ${r.created_by === "ai" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}
                      >
                        {r.created_by === "ai" ? <><Wand2 size={9} className="inline mr-1" />AI</> : "Human"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.hits}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-0.5">
                      <button
                        onClick={() => bumpPriority(r.id, +1)}
                        title="Move up in priority"
                        data-testid={`rule-up-${r.id}`}
                        className="text-slate-500 hover:text-slate-900 p-1"
                      >
                        <ArrowUp size={13} />
                      </button>
                      <button
                        onClick={() => bumpPriority(r.id, -1)}
                        title="Move down in priority"
                        data-testid={`rule-down-${r.id}`}
                        className="text-slate-500 hover:text-slate-900 p-1"
                      >
                        <ArrowDown size={13} />
                      </button>
                      <button
                        onClick={() => patchRule(r.id, { enabled: r.enabled === false })}
                        title={r.enabled === false ? "Enable rule" : "Disable rule"}
                        data-testid={`rule-toggle-${r.id}`}
                        className={`p-1 ${r.enabled === false ? "text-slate-400" : "text-emerald-600"}`}
                      >
                        <Power size={13} />
                      </button>
                      <button
                        onClick={() => setEditRule(r)}
                        title="Edit rule"
                        data-testid={`rule-edit-${r.id}`}
                        className="text-slate-500 hover:text-slate-900 p-1"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => setCopyRule(r)}
                        title="Copy to another company"
                        data-testid={`rule-copy-${r.id}`}
                        className="text-slate-500 hover:text-indigo-600 p-1"
                      >
                        <Copy size={13} />
                      </button>
                      <button onClick={() => del(r.id)} className="text-red-500 p-1" title="Delete">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              };
              // Sort groups alphabetically by display name, preserving key.
              const ordered = [...groups.entries()].sort(([, a], [, b]) =>
                (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
              );
              return (
                <>
                  {ordered.map(([key, g]) => {
                    const childCount = g.children.length;
                    const isCollapsed = collapsedGroups.has(key);
                    return (
                      <React.Fragment key={key}>
                        {g.leader && renderRow(g.leader, false, key, childCount, true)}
                        {!isCollapsed && g.children.map(c => renderRow(c, true, key, 0, false))}
                      </React.Fragment>
                    );
                  })}
                  {!visibleRules.length && (
                    <tr><td colSpan={5} className="text-center py-8 text-slate-500">
                      {rules.length === 0
                        ? "No rules yet."
                        : sourceFilter === "cpa_from_answer"
                          ? "No rules created from client answers yet. When you check \"Save rule\" while acknowledging a client answer, it will show up here."
                          : "No rules match this source filter."}
                    </td></tr>
                  )}
                </>
              );
            })()}
          </tbody>
        </table>
      </div>

      {creating && <CreateRule
        currentId={currentId}
        accts={accts}
        contacts={contacts}
        classes={classes}
        tags={tags}
        setClasses={setClasses}
        setTags={setTags}
        onClose={() => { setCreating(false); load(); }}
      />}

      {editRule && <CreateRule
        currentId={currentId}
        accts={accts}
        contacts={contacts}
        classes={classes}
        tags={tags}
        setClasses={setClasses}
        setTags={setTags}
        editingRule={editRule}
        onClose={() => { setEditRule(null); load(); }}
      />}

      {copyRule && (
        <CopyRuleModal
          rule={copyRule}
          sourceCid={currentId}
          onClose={() => { setCopyRule(null); load(); }}
        />
      )}
    </div>
  );
}


export function CreateRuleModal(props) { return <CreateRule {...props} />; }


function CreateRule({
  currentId, accts, contacts, classes, tags,
  setClasses, setTags, onClose,
  // Queue mode (Mar 2026) — optional inputs prefilled from
  // `/rules/suggest-from-txns`. When `queue` is set the modal shows
  // "N of TOTAL" + Skip/Save & Next controls; when null it's the
  // usual single-shot manual flow.
  initialProposal = null,
  queue = null,     // { current, total, onNext(saved:boolean), onSkip(), onCancel() }
  // Edit mode (Feb 2026) — pass an existing saved rule to open the
  // modal pre-filled for editing. Bottom CTA becomes "Update rule"
  // (PATCH-in-place); no "Save additional" branch.
  editingRule = null,
}) {
  // In edit mode we treat the rule as the "proposal" so all the
  // downstream useState initializers work unchanged.
  const seed = editingRule || initialProposal;
  const [match, setMatch]           = useState(seed?.match_value || "");
  const [matchField, setMatchField] = useState(seed?.match_field || "merchant");
  const [code, setCode]             = useState(seed?.account_code || "");
  // If the proposal only gave us an `account_id` (or a code that our
  // local accounts list doesn't recognize because the CPA renumbered
  // the chart), resolve it by id → code once accounts are loaded.
  useEffect(() => {
    if (code) return;   // already set from account_code
    const wantedId   = initialProposal?.account_id;
    const wantedName = initialProposal?.account_name;
    if (!wantedId && !wantedName) return;
    const list = accts || [];
    const hit = (wantedId && list.find(a => a.id === wantedId))
      || (wantedName && list.find(
        a => (a.name || "").toLowerCase() === wantedName.toLowerCase()));
    if (hit?.code) setCode(hit.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accts]);
  const [applyExisting, setApplyExisting] = useState(true);
  // Inline "New Class" / "New Tag" popups so users never leave the modal.
  const [quickCreate, setQuickCreate] = useState(null);       // "class" | "tag" | null
  // Tier-1 QBO parity conditions + actions.
  const [bankAccountId, setBankAccountId] = useState("");
  // Transaction-type pill — "out" (withdrawals), "in" (deposits), or
  // "both". Defaults from the backend's `direction_hint` on the
  // proposal so buckets that are exclusively withdrawals/deposits
  // pre-select the right pill; mixed buckets land on "both".
  const [direction, setDirection]         = useState(
    editingRule
      ? (editingRule.direction || "both")
      : (initialProposal?.direction_hint || "both"));
  const [amountOp, setAmountOp] = useState(editingRule?.amount_op || "");
  const [amountValue, setAmountValue] = useState(editingRule?.amount_value ?? "");
  const [amountValue2, setAmountValue2] = useState(editingRule?.amount_value_2 ?? "");
  const [contactId, setContactId]   = useState(
    seed?.contact_id
    // Contact-keyed proposals from `suggest-from-txns` carry the
    // contact_id in `match_value` (see rules.py:suggest_rules_from_txns).
    // Fall back to that so the sibling-rules query has the id it needs.
    || (seed?.match_field === "contact"
        ? (seed?.match_value || "")
        : "")
  );
  // Tier-2 QBO parity — multi-condition + Class/Tag actions + posting mode.
  const [conditionLogic, setConditionLogic] = useState(editingRule?.condition_logic || "all");
  const [extraConditions, setExtraConditions] = useState(editingRule?.extra_conditions || []);
  const [classId, setClassId]       = useState(seed?.class_id || "");
  const [tagIds, setTagIds]         = useState(seed?.tag_ids || []);
  const [postingMode, setPostingMode] = useState(seed?.posting_mode || "auto");
  // Tier-3 QBO parity — splits + priority + enabled.
  const [splits, setSplits]         = useState(editingRule?.splits || []);
  const [priority, setPriority]     = useState(seed?.priority ?? 0);
  const [enabled, setEnabled]       = useState(editingRule?.enabled !== false);
  const [markApproved, setMarkApproved] = useState(true);
  const [saving, setSaving] = useState(false);

  // Sibling rules — every rule already saved for the same merchant/
  // contact primary. Fetched once on mount and refreshed whenever the
  // primary changes. Powers the orange "CURRENT" pill + numbered chip
  // strip that lets users see what already exists before saving.
  const [relatedRules, setRelatedRules] = useState([]);
  const [activeChipIdx, setActiveChipIdx] = useState(-1);   // -1 = "new"
  // Snapshot of the rule loaded into the form when a chip is selected —
  // powers the dirty-check that toggles between "Already saved" and the
  // "Save additional / Update current" split.
  const [loadedRuleSnapshot, setLoadedRuleSnapshot] = useState(
    editingRule
      ? {
          id:              editingRule.id,
          account_code:    editingRule.account_code || "",
          direction:       editingRule.direction || "both",
          amount_op:       editingRule.amount_op || "",
          amount_value:    editingRule.amount_value ?? "",
          amount_value_2:  editingRule.amount_value_2 ?? "",
          bank_account_id: editingRule.bank_account_id || "",
          contact_id:      editingRule.contact_id || "",
          class_id:        editingRule.class_id || "",
          tag_ids:         Array.isArray(editingRule.tag_ids) ? [...editingRule.tag_ids] : [],
          posting_mode:    editingRule.posting_mode || "auto",
        }
      : null
  );
  useEffect(() => {
    let cancelled = false;
    const val = (matchField === "contact" ? contactId : match).trim();
    if (!currentId || !val) { setRelatedRules([]); return; }
    api.get(`/companies/${currentId}/rules/related`, {
      params: { match_field: matchField, match_value: val },
    }).then(r => {
      if (!cancelled) setRelatedRules(r.data?.rules || []);
    }).catch(() => { if (!cancelled) setRelatedRules([]); });
    return () => { cancelled = true; };
  }, [currentId, matchField, match, contactId]);

  // Definition B — "exact match" for the CURRENT pill: same primary +
  // filters that actually determine which rows get matched. Cosmetic
  // fields (class, tags, posting_mode) don't count.
  const _isSameRuleAs = (r) => {
    if (!r) return false;
    if (r.account_code !== code) return false;
    const rDir = r.direction || "both";
    const uDir = direction || "both";
    if (rDir !== uDir) return false;
    const rOp = r.amount_op || "";
    if (rOp !== (amountOp || "")) return false;
    if (rOp) {
      if (Number(r.amount_value || 0) !== Number(amountValue || 0)) return false;
      if (rOp === "between"
          && Number(r.amount_value_2 || 0) !== Number(amountValue2 || 0)) return false;
    }
    if ((r.bank_account_id || "") !== (bankAccountId || "")) return false;
    return true;
  };
  const exactMatchRule = relatedRules.find(_isSameRuleAs) || null;
  const hasSiblings = relatedRules.length > 0;
  // Refresh the sibling strip after a delete — cheap re-fetch.
  const refreshRelated = () => {
    const val = (matchField === "contact" ? contactId : match).trim();
    if (!currentId || !val) return;
    api.get(`/companies/${currentId}/rules/related`, {
      params: { match_field: matchField, match_value: val },
    }).then(r => setRelatedRules(r.data?.rules || []))
      .catch(() => {});
  };
  // Delete the entire chip group — the leader rule + every aliased
  // rule that dedupes into it. Matches the user's mental model: chip #1
  // IS the rule, aliases are just DB-level noise, so trash = delete
  // the whole thing.
  const deleteChipGroup = async (chip) => {
    if (!chip) return;
    const ids = [chip.id, ...((chip.aliases || []).map(a => a.id))].filter(Boolean);
    if (!ids.length) return;
    const dest = chip.account_name || chip.account_code;
    const msg = ids.length === 1
      ? `Delete this rule (routes to ${dest})?`
      : `Delete all ${ids.length} rules that route to ${dest}?`;
    if (!window.confirm(msg)) return;
    try {
      await Promise.all(
        ids.map(rid => api.delete(`/companies/${currentId}/rules/${rid}`)),
      );
      toast.success(ids.length === 1 ? "Rule deleted" : `${ids.length} rules deleted`);
      setActiveChipIdx(-1);
      refreshRelated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };
  // Load an existing sibling into the form. Snapshots the rule so we
  // can dirty-check against the form and offer "Update current rule"
  // when the CPA tweaks anything (Feb 2026).
  const loadRule = (r, idx) => {
    setActiveChipIdx(idx);
    setCode(r.account_code || "");
    setDirection(r.direction || "both");
    setAmountOp(r.amount_op || "");
    setAmountValue(r.amount_value ?? "");
    setAmountValue2(r.amount_value_2 ?? "");
    setBankAccountId(r.bank_account_id || "");
    if (r.contact_id) setContactId(r.contact_id);
    if (r.class_id)  setClassId(r.class_id);
    setTagIds(Array.isArray(r.tag_ids) ? [...r.tag_ids] : []);
    setPostingMode(r.posting_mode || "auto");
    setLoadedRuleSnapshot({
      id:              r.id,
      account_code:    r.account_code || "",
      direction:       r.direction || "both",
      amount_op:       r.amount_op || "",
      amount_value:    r.amount_value ?? "",
      amount_value_2:  r.amount_value_2 ?? "",
      bank_account_id: r.bank_account_id || "",
      contact_id:      r.contact_id || "",
      class_id:        r.class_id || "",
      tag_ids:         Array.isArray(r.tag_ids) ? [...r.tag_ids] : [],
      posting_mode:    r.posting_mode || "auto",
    });
  };
  // Any change vs the loaded snapshot flips the CTA into split mode.
  const isDirtyVsLoadedRule = (() => {
    const s = loadedRuleSnapshot;
    if (!s) return false;
    const tagsEqual = (a, b) => {
      const A = [...(a || [])].sort();
      const B = [...(b || [])].sort();
      return A.length === B.length && A.every((v, i) => v === B[i]);
    };
    return (
      s.account_code    !== (code || "")
      || s.direction    !== (direction || "both")
      || s.amount_op    !== (amountOp || "")
      || String(s.amount_value)   !== String(amountValue ?? "")
      || String(s.amount_value_2) !== String(amountValue2 ?? "")
      || s.bank_account_id !== (bankAccountId || "")
      || s.contact_id      !== (contactId || "")
      || s.class_id        !== (classId || "")
      || !tagsEqual(s.tag_ids, tagIds)
      || s.posting_mode    !== (postingMode || "auto")
    );
  })();
  // PATCH the loaded rule in place. Uses the extended /rules/{rid}
  // endpoint that accepts the full writable field set. If the loaded
  // chip represents a group (leader + aliases), we PATCH ALL of them
  // with the same action/condition-filter payload — the chip is the
  // user's mental model of "one rule", so updating it must overwrite
  // every deduped duplicate too.
  const updateCurrentRule = async () => {
    if (!loadedRuleSnapshot?.id || saving) return;
    setSaving(true);
    try {
      const payload = {
        account_code:  code,
        direction:     direction === "both" ? null : direction,
        amount_op:     amountOp || "",
        amount_value:  amountOp ? Number(amountValue || 0) : null,
        amount_value_2: (amountOp === "between") ? Number(amountValue2 || 0) : null,
        bank_account_id: bankAccountId || null,
        contact_id:      (contactId && matchField !== "contact") ? contactId : null,
        class_id:        classId || null,
        tag_ids:         tagIds,
        posting_mode:    postingMode,
        condition_logic: conditionLogic,
      };
      // Edit mode lets the CPA fix typos in the match string itself.
      if (editingRule) {
        payload.match_field = matchField;
        payload.match_value = match;
        payload.enabled     = enabled;
        payload.priority    = Number(priority || 0);
      }
      if (extraConditions.length) {
        payload.extra_conditions = extraConditions
          .filter(c => c.field && c.op && c.value !== "")
          .map(c => ({
            field:   c.field,
            op:      c.op,
            value:   String(c.value),
            ...(c.op === "between" && c.value_2 !== ""
                ? { value_2: Number(c.value_2) } : {}),
          }));
      }
      // Propagate to leader + aliases.
      const chip = relatedRules[activeChipIdx];
      const ids = chip
        ? [chip.id, ...((chip.aliases || []).map(a => a.id))].filter(Boolean)
        : [loadedRuleSnapshot.id];
      await Promise.all(
        ids.map(rid => api.patch(
          `/companies/${currentId}/rules/${rid}`, payload,
        )),
      );
      toast.success(ids.length === 1
        ? "Rule updated"
        : `${ids.length} rules updated`);
      if (queue) queue.onNext(true);
      else onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update rule");
    } finally {
      setSaving(false);
    }
  };

  const addCondition = () => setExtraConditions(
    (cs) => [...cs, { field: "description", op: "contains", value: "", value_2: "" }]
  );
  const patchCondition = (i, patch) => setExtraConditions(
    (cs) => cs.map((c, idx) => idx === i ? { ...c, ...patch } : c)
  );
  const removeCondition = (i) => setExtraConditions(
    (cs) => cs.filter((_, idx) => idx !== i)
  );

  // Split the CoA into bank/asset options (for the condition) and
  // categorization targets (for the action). Asset+liability parents like
  // Chase Business / BofA Credit Card land in the bank filter.
  const bankOptions = (accts || []).filter(a =>
    ["asset", "liability"].includes((a.type || "").toLowerCase())
    && !a.is_parent
  );
  const categoryOptions = accts || [];

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        match_type: matchField === "contact" ? "contact_equals" : "merchant_contains",
        match_field: matchField,
        match_value: match,
        account_code: code,
        apply_to_existing: applyExisting,
        posting_mode: postingMode,
        condition_logic: conditionLogic,
      };
      if (bankAccountId) payload.bank_account_id = bankAccountId;
      if (direction && direction !== "both") payload.direction = direction;   // "in" | "out"
      // Contact ACTION is meaningless when the CONDITION already keys
      // on contact — the row already carries the contact id. Skip it.
      if (contactId && matchField !== "contact") payload.contact_id = contactId;
      if (classId)       payload.class_id        = classId;
      if (tagIds.length) payload.tag_ids         = tagIds;
      if (amountOp) {
        payload.amount_op    = amountOp;
        payload.amount_value = Number(amountValue);
        if (amountOp === "between") payload.amount_value_2 = Number(amountValue2);
      }
      if (extraConditions.length) {
        payload.extra_conditions = extraConditions
          .filter(c => c.field && c.op && c.value !== "")
          .map(c => ({
            field:   c.field,
            op:      c.op,
            value:   String(c.value),
            ...(c.op === "between" && c.value_2 !== ""
                ? { value_2: Number(c.value_2) } : {}),
          }));
      }
      // Tier-3 fields — always sent so the backend can persist them.
      payload.priority = Number(priority || 0);
      payload.enabled  = !!enabled;
      payload.mark_approved = !!markApproved;   // opt-out via footer checkbox
      const cleanSplits = splits
        .filter(s => s.account_code && Number(s.percent) > 0)
        .map(s => ({ account_code: s.account_code,
                      percent: Number(s.percent) }));
      if (cleanSplits.length) payload.splits = cleanSplits;
      const r = await api.post(`/companies/${currentId}/rules`, payload);
      toast.success(`Rule created · applied to ${r.data.applied} existing`);
      if (queue) queue.onNext(true);
      else onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create rule");
    } finally {
      setSaving(false);
    }
  };

  const disabled = !match || !code || saving
    || (amountOp && amountValue === "")
    || (amountOp === "between" && amountValue2 === "");

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold">
              {queue ? "Suggested rule" : "Create Rule"}
            </h3>
          {queue && (
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={() => queue.onPrev && queue.onPrev()}
                disabled={queue.current <= 1}
                data-testid="queue-prev"
                className="text-slate-500 hover:text-slate-900 disabled:opacity-30"
                title="Previous suggestion"
              >
                <ChevronDown size={14} className="rotate-90" />
              </button>
              <p className="text-xs text-slate-500" data-testid="queue-header">
                {queue.current} of {queue.total}
                {initialProposal?.covered_txn_count > 0
                  && ` · covers ${initialProposal.covered_txn_count} selected txn${initialProposal.covered_txn_count === 1 ? "" : "s"}`}
              </p>
              <button
                onClick={() => queue.onSkip()}
                disabled={queue.current >= queue.total}
                data-testid="queue-skip-top"
                className="text-slate-500 hover:text-slate-900 disabled:opacity-30"
                title="Skip to next suggestion"
              >
                <ChevronDown size={14} className="-rotate-90" />
              </button>
            </div>
          )}
          </div>
          <div className="flex items-center gap-2">
            {hasSiblings && (
              <div className="flex items-center gap-1.5" data-testid="rule-current-pill-wrap">
                {relatedRules.length > 1 && (
                  <div className="flex items-center gap-0.5" data-testid="rule-sibling-chips">
                    {relatedRules.map((r, i) => {
                      const memberCount = 1 + (r.aliases_count || 0);
                      const isActive = activeChipIdx === i;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => loadRule(r, i)}
                          data-testid={`rule-sibling-chip-${i}`}
                          title={`${r.match_value_display || r.match_value} → ${r.account_name || r.account_code}${r.direction ? ` · ${r.direction === "out" ? "Withdrawal" : "Deposit"}` : ""}${memberCount > 1 ? `  (represents ${memberCount} equivalent rules)` : ""}`}
                          className={`w-5 h-5 text-[10px] font-mono-num rounded-full border ${
                            isActive
                              ? "border-orange-500 bg-orange-500 text-white"
                              : "border-slate-300 bg-white text-slate-600 hover:border-orange-400"
                          }`}
                        >
                          {i + 1}
                        </button>
                      );
                    })}
                  </div>
                )}
                <span
                  data-testid="rule-current-pill"
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                    exactMatchRule
                      ? "border-orange-500 bg-orange-100 text-orange-700"
                      : "border-slate-300 bg-slate-50 text-slate-600"
                  }`}
                  title={exactMatchRule
                    ? "A rule with exactly these settings already exists"
                    : `${relatedRules.length} existing rule${relatedRules.length === 1 ? "" : "s"} for this ${matchField}`}
                >
                  {exactMatchRule ? "Current" : `${relatedRules.length} existing`}
                </span>
              </div>
            )}
            <button onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        {/* ---- CONDITIONS ---- */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              When a transaction matches
            </div>
            {(extraConditions.length > 0) && (
              <div className="flex rounded-md border overflow-hidden text-[10px]">
                {["all", "any"].map(v => (
                  <button
                    key={v}
                    onClick={() => setConditionLogic(v)}
                    data-testid={`rule-logic-${v}`}
                    className={`px-2 py-0.5 uppercase font-medium ${
                      conditionLogic === v
                        ? "bg-slate-900 text-white"
                        : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  >
                    {v === "all" ? "ALL" : "ANY"}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Feature A — primary field selector. Merchant = regex on
              the raw bank string. Contact = exact match on contact_id
              (cleaner for well-curated books). */}
          {Array.isArray(contacts) && contacts.length > 0 && (
            <div className="flex rounded-md border overflow-hidden text-[10px] w-fit mb-2">
              {[
                { v: "merchant", label: "Merchant" },
                { v: "contact",  label: "Contact"  },
              ].map(o => (
                <button
                  key={o.v}
                  onClick={() => {
                    setMatchField(o.v);
                    setMatch("");   // reset value when swapping types
                  }}
                  data-testid={`rule-match-field-${o.v}`}
                  className={`px-2.5 py-1 uppercase font-medium tracking-wider ${
                    matchField === o.v
                      ? "bg-slate-900 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {matchField === "contact" ? (
            <select
              value={match}
              onChange={(e) => setMatch(e.target.value)}
              data-testid="rule-match-value"
              className="w-full border rounded px-3 py-2 text-sm"
            >
              <option value="">Contact…</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          ) : (
            <input
              placeholder="Merchant contains (e.g. Uber)"
              value={match}
              onChange={(e) => setMatch(e.target.value)}
              data-testid="rule-match-value"
              className="w-full border rounded px-3 py-2 text-sm"
            />
          )}

          {/* Transaction-type pills. Sits between the match value and
              the bank/amount grid so users can quickly narrow the rule
              to withdrawals or deposits without touching the amount
              operator below. Auto-selects from the proposal's
              direction_hint (matches the underlying rows' sign). */}
          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Transaction type
            </div>
            <div className="inline-flex items-center gap-1" data-testid="rule-direction-pills">
              {[
                { key: "out",  label: "Withdrawal" },
                { key: "in",   label: "Deposit"    },
                { key: "both", label: "Both"       },
              ].map(p => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`rule-direction-${p.key}`}
                  onClick={() => setDirection(p.key)}
                  className={`px-2.5 py-1 text-xs rounded-md border ${
                    direction === p.key
                      ? (p.key === "out"  ? "border-rose-600 bg-rose-600 text-white"
                        : p.key === "in"  ? "border-emerald-600 bg-emerald-600 text-white"
                        :                   "border-slate-900 bg-slate-900 text-white")
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Bank account <span className="text-slate-400 normal-case font-normal">(optional)</span>
              </label>
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                data-testid="rule-bank-account"
                className="w-full border rounded px-2 py-1.5 text-sm"
              >
                <option value="">Any account</option>
                {bankOptions.map(a => (
                  <option key={a.id} value={a.id}>{a.code} {a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Amount <span className="text-slate-400 normal-case font-normal">(optional)</span>
              </label>
              <div className="flex gap-1">
                <select
                  value={amountOp}
                  onChange={(e) => setAmountOp(e.target.value)}
                  data-testid="rule-amount-op"
                  className="border rounded px-1.5 py-1.5 text-sm w-24"
                >
                  <option value="">any</option>
                  <option value="gt">{">"}</option>
                  <option value="lt">{"<"}</option>
                  <option value="eq">{"="}</option>
                  <option value="between">between</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  placeholder={amountOp === "between" ? "min" : "value"}
                  value={amountValue}
                  onChange={(e) => setAmountValue(e.target.value)}
                  disabled={!amountOp}
                  data-testid="rule-amount-value"
                  className="flex-1 border rounded px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                />
                {amountOp === "between" && (
                  <input
                    type="number"
                    step="0.01"
                    placeholder="max"
                    value={amountValue2}
                    onChange={(e) => setAmountValue2(e.target.value)}
                    data-testid="rule-amount-value-2"
                    className="w-24 border rounded px-2 py-1.5 text-sm"
                  />
                )}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                Signed — deposits {">"}0, withdrawals {"<"}0
              </div>
            </div>
          </div>

          {/* Tier-2: extra conditions builder. Text + amount + bank. */}
          {extraConditions.map((c, i) => (
            <div key={i} className="mt-2 flex items-center gap-1"
                 data-testid={`rule-extra-condition-${i}`}>
              <select
                value={c.field}
                onChange={(e) => patchCondition(i, {
                  field: e.target.value,
                  op: e.target.value === "amount" ? "gt"
                    : e.target.value === "bank_account" ? "equals" : "contains",
                  value: "", value_2: "",
                })}
                className="border rounded px-1 py-1 text-xs w-28"
              >
                <option value="merchant">Merchant</option>
                <option value="description">Description</option>
                <option value="amount">Amount</option>
                <option value="bank_account">Bank acct</option>
              </select>
              <select
                value={c.op}
                onChange={(e) => patchCondition(i, { op: e.target.value })}
                className="border rounded px-1 py-1 text-xs w-28"
              >
                {c.field === "amount" ? (
                  <>
                    <option value="gt">{">"}</option>
                    <option value="lt">{"<"}</option>
                    <option value="eq">{"="}</option>
                    <option value="between">between</option>
                  </>
                ) : c.field === "bank_account" ? (
                  <option value="equals">is</option>
                ) : (
                  <>
                    <option value="contains">contains</option>
                    <option value="not_contains">doesn't contain</option>
                    <option value="starts_with">starts with</option>
                    <option value="ends_with">ends with</option>
                    <option value="equals">equals</option>
                  </>
                )}
              </select>
              {c.field === "bank_account" ? (
                <select
                  value={c.value}
                  onChange={(e) => patchCondition(i, { value: e.target.value })}
                  className="flex-1 border rounded px-2 py-1 text-xs"
                >
                  <option value="">Pick account…</option>
                  {bankOptions.map(a => (
                    <option key={a.id} value={a.id}>{a.code} {a.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  value={c.value}
                  onChange={(e) => patchCondition(i, { value: e.target.value })}
                  placeholder={c.field === "amount" ? "value" : "text"}
                  type={c.field === "amount" ? "number" : "text"}
                  step="0.01"
                  className="flex-1 border rounded px-2 py-1 text-xs"
                />
              )}
              {c.field === "amount" && c.op === "between" && (
                <input
                  value={c.value_2}
                  onChange={(e) => patchCondition(i, { value_2: e.target.value })}
                  placeholder="max"
                  type="number"
                  step="0.01"
                  className="w-16 border rounded px-2 py-1 text-xs"
                />
              )}
              <button
                onClick={() => removeCondition(i)}
                data-testid={`rule-extra-remove-${i}`}
                className="text-slate-400 hover:text-rose-600"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <button
            onClick={addCondition}
            data-testid="rule-add-condition"
            className="mt-2 text-[11px] text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
          >
            <Plus size={11} /> Add condition
          </button>
        </div>

        {/* ---- ACTIONS ---- */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
            Then apply
          </div>
          <select
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid="rule-category"
            className="w-full border rounded px-3 py-2 text-sm"
          >
            <option value="">Category…</option>
            {categoryOptions.map(a => (
              <option key={a.id} value={a.code}>{a.code} {a.name}</option>
            ))}
          </select>

          {/* Tier-3: multi-category split builder. Rows sum to 100. */}
          {splits.length > 0 && (
            <div className="mt-2 space-y-1"
                 data-testid="rule-splits">
              {splits.map((s, i) => (
                <div key={i} className="flex items-center gap-1"
                     data-testid={`rule-split-row-${i}`}>
                  <select
                    value={s.account_code}
                    onChange={(e) => setSplits(prev => prev.map((x, idx) =>
                      idx === i ? { ...x, account_code: e.target.value } : x))}
                    className="flex-1 border rounded px-2 py-1 text-xs"
                  >
                    <option value="">Category…</option>
                    {categoryOptions.map(a => (
                      <option key={a.id} value={a.code}>{a.code} {a.name}</option>
                    ))}
                  </select>
                  <input
                    type="number" step="0.01" min="0" max="100"
                    value={s.percent}
                    onChange={(e) => setSplits(prev => prev.map((x, idx) =>
                      idx === i ? { ...x, percent: e.target.value } : x))}
                    className="w-16 border rounded px-2 py-1 text-xs text-right"
                    placeholder="%"
                  />
                  <span className="text-xs text-slate-400">%</span>
                  <button
                    onClick={() => setSplits(prev => prev.filter((_, idx) => idx !== i))}
                    className="text-slate-400 hover:text-rose-600"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <div className="text-[10px] text-right text-slate-500">
                Sum: {splits.reduce((n, s) => n + (Number(s.percent) || 0), 0)}% · must be 100%
              </div>
            </div>
          )}
          <button
            onClick={() => setSplits(prev => [...prev, { account_code: "", percent: "" }])}
            data-testid="rule-add-split"
            className="mt-1 text-[11px] text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
          >
            <Plus size={11} /> {splits.length === 0 ? "Split into multiple categories" : "Add split"}
          </button>

          {Array.isArray(contacts) && contacts.length > 0 && matchField !== "contact" && (
            <select
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              data-testid="rule-contact"
              className="w-full border rounded px-3 py-2 text-sm mt-2"
            >
              <option value="">Contact (optional)…</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}

          {Array.isArray(classes) && classes.length > 0 ? (
            <select
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              data-testid="rule-class"
              className="w-full border rounded px-3 py-2 text-sm mt-2"
            >
              <option value="">Class (optional)…</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          ) : (
            <div
              data-testid="rule-class-empty"
              className="w-full border rounded px-3 py-2 text-sm mt-2 bg-slate-50 text-slate-500 flex items-center justify-between"
            >
              <span>Class (optional)</span>
              <button
                type="button"
                onClick={() => setQuickCreate("class")}
                data-testid="rule-class-create"
                className="text-[11px] text-emerald-700 hover:underline"
              >
                No classes yet — create one →
              </button>
            </div>
          )}

          {Array.isArray(tags) && tags.length > 0 ? (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Tags (optional)
              </div>
              <div className="flex flex-wrap gap-1">
                {tags.map(t => {
                  const on = tagIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTagIds(prev =>
                        on ? prev.filter(x => x !== t.id) : [...prev, t.id])}
                      data-testid={`rule-tag-${t.id}`}
                      className={`px-2 py-0.5 rounded-full text-[11px] border ${
                        on
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div
              data-testid="rule-tags-empty"
              className="w-full border rounded px-3 py-2 text-sm mt-2 bg-slate-50 text-slate-500 flex items-center justify-between"
            >
              <span>Tags (optional)</span>
              <button
                type="button"
                onClick={() => setQuickCreate("tag")}
                data-testid="rule-tag-create"
                className="text-[11px] text-emerald-700 hover:underline"
              >
                No tags yet — create one →
              </button>
            </div>
          )}

          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Posting mode
            </div>
            <div className="flex rounded-md border overflow-hidden text-xs w-fit">
              {[
                { v: "auto",   label: "Auto-add",       hint: "Post immediately" },
                { v: "review", label: "Flag for review", hint: "Send to CPA queue" },
              ].map(o => (
                <button
                  key={o.v}
                  onClick={() => setPostingMode(o.v)}
                  data-testid={`rule-posting-${o.v}`}
                  title={o.hint}
                  className={`px-3 py-1 ${
                    postingMode === o.v
                      ? "bg-slate-900 text-white"
                      : "bg-white text-slate-700 hover:bg-slate-50"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <label className="text-xs flex items-center gap-2">
          <input
            type="checkbox"
            checked={applyExisting}
            onChange={(e) => setApplyExisting(e.target.checked)}
          />
          Apply to existing unreviewed transactions
        </label>
        {applyExisting && (
          <label className="text-xs flex items-center gap-2 -mt-1 ml-5 text-slate-600">
            <input
              type="checkbox"
              checked={markApproved}
              onChange={(e) => setMarkApproved(e.target.checked)}
              data-testid="rule-mark-approved"
            />
            Also mark applied rows as approved
          </label>
        )}
        <div className="flex items-center gap-2">
          {queue && (
            <button
              onClick={() => queue.onSkip()}
              disabled={saving}
              data-testid="queue-skip"
              className="px-3 py-2 rounded-md border text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Skip
            </button>
          )}
          {/* When a chip is loaded and the user has tweaked the form,
              split the CTA into "Update current" (PATCH the loaded
              rule) and "Save additional" (POST a new rule). Otherwise
              a single primary CTA. Edit mode: always single "Update
              rule" button, no "Save additional" branch. */}
          {editingRule ? (
            <button
              data-testid="rule-update-current-btn"
              onClick={updateCurrentRule}
              disabled={disabled}
              className="flex-1 py-2 rounded-md bg-slate-900 text-white text-sm disabled:opacity-50"
            >
              {saving ? "Updating…" : "Update rule"}
            </button>
          ) : loadedRuleSnapshot && isDirtyVsLoadedRule && !exactMatchRule ? (
            <>
              <button
                data-testid="rule-update-current-btn"
                onClick={updateCurrentRule}
                disabled={disabled}
                className="flex-1 py-2 rounded-md border border-slate-900 bg-white text-slate-900 text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                {saving ? "Updating…" : "Update current rule"}
              </button>
              <button
                data-testid={TID.saveBtn}
                onClick={save}
                disabled={disabled}
                className="flex-1 py-2 rounded-md bg-slate-900 text-white text-sm disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save additional rule"}
              </button>
            </>
          ) : (
            <button
              data-testid={TID.saveBtn}
              onClick={save}
              disabled={disabled || !!exactMatchRule}
              title={exactMatchRule ? "A rule with these exact settings already exists" : undefined}
              className="flex-1 py-2 rounded-md bg-slate-900 text-white text-sm disabled:opacity-50"
            >
              {saving ? "Saving…"
                : exactMatchRule
                  ? "Already saved"
                  : hasSiblings
                    ? (queue
                        ? (queue.current < queue.total ? "Save additional & next" : "Save additional rule")
                        : "Save additional rule")
                    : (queue
                        ? (queue.current < queue.total ? "Save & next" : "Save & done")
                        : "Save rule")}
            </button>
          )}
          {/* Contextual trash — sits to the right of the primary CTA.
              Only visible when a chip is active; deletes that chip's
              group (leader + all aliases). */}
          {activeChipIdx >= 0 && relatedRules[activeChipIdx] && (() => {
            const g = relatedRules[activeChipIdx];
            const groupSize = 1 + (g.aliases_count || 0);
            return (
              <button
                type="button"
                onClick={() => deleteChipGroup(g)}
                disabled={saving}
                data-testid="rule-active-chip-delete"
                title={groupSize > 1
                  ? `Delete all ${groupSize} rules that route to ${g.account_name || g.account_code}`
                  : `Delete this rule (routes to ${g.account_name || g.account_code})`}
                className="p-2 rounded-md border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-300 hover:bg-rose-50 disabled:opacity-50"
              >
                <Trash2 size={16} />
              </button>
            );
          })()}
        </div>
      </div>

      {quickCreate && (
        <QuickCreateModal
          kind={quickCreate}
          currentId={currentId}
          onClose={() => setQuickCreate(null)}
          onCreated={({ id, name }) => {
            if (quickCreate === "class") {
              setClasses(prev => [...prev, { id, name }]);
              setClassId(id);
            } else {
              setTags(prev => [...prev, { id, name }]);
              setTagIds(prev => [...prev, id]);
            }
            setQuickCreate(null);
            toast.success(`${quickCreate === "class" ? "Class" : "Tag"} "${name}" created`);
          }}
        />
      )}
    </div>
  );
}
